import { getSaiTerminology } from '../../../config/terminology'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useState, useEffect } from 'react';

import useAxiosPrivateSAI from '../../../hooks/useAxiosPrivateSAI';
import useAuth from '../../../hooks/useAuth'
import { validateClaim } from './claimRules';

import StepThree from './Partials/StepThree'
import StepTwo from './Partials/StepTwo';
import StepOne from './Partials/StepOne';

import ClaimConfirmation from './ClaimConfirmation'
import ClaimLoader from './Components/ClaimLoader'

const CreateClaim = ({ onSuccess }) => {
  const { auth, createInfo } = useAuth()

  const axios = useAxiosPrivateSAI()

  const [policyData, setPolicyData] = useState(null)
  const [department, setDepartment] = useState(null)
  const mutualGbs = policyData?.department === "Mutual" || policyData?.department === "GBS"
  const terms = getSaiTerminology(policyData)

  const [isStepOneValidState, setIsStepOneValidState] = useState(false)
  const [stepOneErrors, setStepOneErrors] = useState({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(true)
  const [loading, setLoading] = useState(false)
  const [isStepLoading, setIsStepLoading] = useState(true)
  const [orgType, setOrgType] = useState(null)
  const [claim, setClaim] = useState({})
  const [step, setStep] = useState(1)

  const [formData, setFormData] = useState({
    stepOne: {},
    stepTwo: {},
    stepThree: {},
  });

  const nextStep = () => {
    setIsStepLoading(step === 1);
    setStep((prev) => Math.min(prev + 1, 4));
  };
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  useEffect(() => {
    const storedPolicy = JSON.parse(localStorage.getItem('policyData'))
    const storedOrg = JSON.parse(localStorage.getItem('orgType'))
    setPolicyData(storedPolicy)
    setDepartment(storedPolicy.department)
    setOrgType(storedOrg)
  }, [])

  useEffect(() => {
    const errors = validateClaim(formData.stepOne, policyData);
    setStepOneErrors(errors);
    setIsStepOneValidState(Object.keys(errors).length === 0);
  }, [formData.stepOne, policyData]);

  const isStepTwoValid = () => {
    const model = formData.stepTwo?.model || {}
    return (
      model.continuous_absence &&
      model.twenty_working_days &&
      model.parental_leave_planned &&
      model.upcoming_disciplinary_action &&
      model.supporting_documentation
    )
  }

  const isStepThreeValid = () => {
    const data = formData.stepThree;

    return data?.accepted === true;
  };

  const isCurrentStepValid = () => {
    switch (step) {
      case 1:
        return isStepOneValidState
      case 2:
        return isStepTwoValid();
      case 3:
        return isStepThreeValid();
      default:
        return false;
    }
  }

  const handleNext = async () => {
    if (isStepLoading || loading || !isCurrentStepValid()) return

    setLoading(true)

    try {
      switch (step) {
        case 1:
          await submitStepOne()
          break;
        case 2:
          await submitStepTwo()
          break;
        case 3:
          await submitStepThree()
          break;
        default:
          break;
      }
    } finally {
      setLoading(false)
    }
  }

  const submitStepOne = async () => {
    if (Object.keys(validateClaim(formData.stepOne, policyData)).length) return;
    const {
      selectedStaff,
      selectedCondition,
      absenceStart,
      absenceEnd,
      isPhasedReturn,
      isShielding,
      isConfidential
    } = formData.stepOne

    if (!selectedStaff?.staff_id || !selectedCondition?.condition_id || !absenceStart) {
      console.warn("Missing required fields for Step One submission.")
      return;
    }

    const payload = {
      staff_id: selectedStaff.staff_id,
      condition_id: selectedCondition.condition_id,
      policy_id: policyData.policy_id,
      claim_absence_start_date: absenceStart,
      claim_absence_end_date: absenceEnd || null,
      claim_phased: isPhasedReturn ? 1 : 0,
      shielding: isShielding ? 1 : 0,
      confidential: isConfidential ? 1 : 0,
      claim_status_id: 1,
      claim_information: '{}',
      updated_by: auth?.user?.contact_id
    };

    try {
      const res = await axios.post(`/api/v1/staff-hub/claims?api_token=${process.env.REACT_APP_KIS_TOKEN}`, payload);
      if (res?.data?.claim_id) {
        const newClaim = res.data

        setClaim(newClaim)

        setFormData((prev) => ({
          ...prev,
          stepOne: {
            ...prev.stepOne,
            claim_id: newClaim.claim_id
          }
        }));

        nextStep()
      } else {
        console.error("Claim created, but no claim_id returned.")
      }
    } catch (err) {
      console.error("Failed to submit Step One", err)
    }
  };


  const submitStepTwo = async () => {
    const claimId = claim?.claim_id || formData?.stepOne?.claim_id
    const stepTwo = formData?.stepTwo

    if (!claimId || !stepTwo) {
      console.warn("Missing claim ID or step two data.")
      return
    }

    const { model = {} } = stepTwo;

    // Validate the same model fields as the Next Step button.
    if (!isStepTwoValid()) {
      console.warn("Incomplete Step Two data.")
      return
    }

    const claim_information = JSON.stringify(model)

    const payload = {
      claim_information,
    };

    try {
      const res = await axios.post(`/api/v1/staff-hub/claims/update/${claimId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`, payload)
      if (res.status === 200 || res.status === 201 || res.status === 204) {
        nextStep()
      } else {
        console.error("Unexpected response while submitting Step Two.")
      }
    } catch (err) {
      console.error("Failed to submit Step Two", err)
    }
  }

  const submitStepThree = async () => {
    const claimId = claim?.claim_id || formData?.stepOne?.claim_id

    if (!claimId) {
      createInfo('warning', 'No claim ID available to calculate the claim.')
      return
    }

    try {
      setSubmitting(true)
      const res = await axios.get(`/api/v1/staff-hub/claims/calculate/${claimId}?api_token=${process.env.REACT_APP_KIS_TOKEN}`)
      if (res.status === 200) {
        createInfo('success', 'Claim successfully calculated.')
        nextStep()
        setSubmitted(true)
        if (onSuccess) onSuccess()
      } else {
        createInfo('error', 'Unexpected response from calculate endpoint.')
      }
    } catch (err) {
      createInfo('error', 'Failed to calculate claim')
    }
    setSubmitting(false)
  }

  if (!policyData) return <ClaimLoader />;

  return (
    <div className="space-y-4 p-6">
      {step >= 1 && step <= 3 && (
        <div>
          <h2 className="text-xl font-bold text-sas mb-6">Create {terms.claim.singularTitle}</h2>

          {step === 1 && (
            <StepOne
              onLoadingChange={setIsStepLoading}
              data={formData.stepOne || {}}
              onUpdate={(data) =>
                setFormData((prev) => {
                  if (
                    prev.stepOne?.selectedCondition?.condition_id !==
                    data?.selectedCondition?.condition_id
                  ) {
                    return { ...prev, stepOne: data, stepTwo: {} };
                  }
                  return { ...prev, stepOne: data };
                })
              }
              policyId={policyData?.policy_id}
              policy={policyData}
              errors={stepOneErrors}
              mutualGbs={mutualGbs}
              claim={null}
            />
          )}
          {step === 2 && (
            <StepTwo
              onLoadingChange={setIsStepLoading}
              data={formData.stepTwo}
              onUpdate={(data) =>
                setFormData((prev) => ({ ...prev, stepTwo: data }))
              }
              conditionId={formData?.stepOne?.selectedCondition?.condition_id}
              policy={policyData}
              mutualGbs={mutualGbs}
              claim={null}
            />
          )}
          {step === 3 && (
            <StepThree
              data={formData.stepThree}
              onUpdate={(data) =>
                setFormData((prev) => ({ ...prev, stepThree: data }))
              }
            />
          )}

          <div className={`flex justify-between mt-8 ${isStepLoading ? 'hidden' : ''}`}>
            {step < 3 ? (
              loading ? (
                <button
                  disabled
                  className="px-4 py-2 text-sm font-medium text-white bg-sas rounded-md flex items-center justify-center"
                >
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2 text-xl" />
                  Loading...
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleNext}
                  disabled={!isCurrentStepValid()}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md ${isCurrentStepValid() ? 'bg-sas hover:bg-sas-dark' : 'bg-gray-400 cursor-not-allowed'
                    }`}
                >
                  Next Step
                </button>
              )
            ) : (
              <>
                {formData.stepThree?.accepted && (
                  <button onClick={submitStepThree} disabled={submitting} className="px-4 py-2 text-sm font-medium text-white bg-sas rounded-md hover:bg-sas-dark">
                    {submitting ? 'Submitting...' : 'Submit'}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {step === 4 && <ClaimConfirmation department={department} />}
    </div>
  )
}

export default CreateClaim
